/* ==========================================================================
   Future Ecom — scroll-triggered reveal
   CLAUDE.md §Design direction → ecommerce.com.au: "nearly every block fades
   and rises into view on scroll. IntersectionObserver + a CSS transform/opacity
   transition — no animation library needed."

   Shared across all 12 sections. Add `.fe-reveal` to any element; set
   `--fe-reveal-delay` inline to stagger a group.

   Reduced motion is honoured here as well as in CSS: elements are marked
   visible immediately and no observer is created at all.
   ========================================================================== */

(function () {
  var VISIBLE = 'fe-is-visible';
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function revealAll(scope) {
    var els = (scope || document).querySelectorAll('.fe-reveal');
    for (var i = 0; i < els.length; i++) {
      els[i].classList.add(VISIBLE);
    }
  }

  function init(scope) {
    var root = scope || document;
    var els = root.querySelectorAll('.fe-reveal:not(.' + VISIBLE + ')');
    if (!els.length) return;

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      revealAll(root);
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        for (var i = 0; i < entries.length; i++) {
          if (entries[i].isIntersecting) {
            entries[i].target.classList.add(VISIBLE);
            obs.unobserve(entries[i].target);
          }
        }
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );

    for (var j = 0; j < els.length; j++) {
      observer.observe(els[j]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor: sections are re-rendered without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  // If the visitor turns reduced motion on mid-session, stop hiding things.
  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', function (event) {
      if (event.matches) revealAll(document);
    });
  }
})();

/* ==========================================================================
   Per-letter heading reveal
   CLAUDE.md: "Section headings animate in letter by letter with a short
   stagger. Use sparingly — one or two headings, not all twelve, or the page
   feels gimmicky."

   The cap is enforced here, not left to discipline: only the first two
   [data-fe-letters] headings on the page are ever split.

   Accessibility: the heading keeps its full text as an aria-label and every
   generated span is aria-hidden, so assistive tech reads the sentence once
   rather than spelling it out.
   ========================================================================== */

(function () {
  var MAX_HEADINGS = 2;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  function split(el) {
    var text = (el.textContent || '').trim();
    if (!text) return false;

    el.setAttribute('aria-label', text);

    var frag = document.createDocumentFragment();
    var words = text.split(/\s+/);
    var index = 0;

    words.forEach(function (word, wordIndex) {
      var wordEl = document.createElement('span');
      wordEl.className = 'fe-letters__word';
      wordEl.setAttribute('aria-hidden', 'true');

      for (var i = 0; i < word.length; i++) {
        var charEl = document.createElement('span');
        charEl.className = 'fe-letters__char';
        charEl.style.setProperty('--fe-char-index', index++);
        charEl.textContent = word.charAt(i);
        wordEl.appendChild(charEl);
      }

      frag.appendChild(wordEl);
      // a real space between words so the heading still wraps normally
      if (wordIndex < words.length - 1) {
        frag.appendChild(document.createTextNode(' '));
      }
    });

    el.textContent = '';
    el.appendChild(frag);
    el.classList.add('fe-letters');
    return true;
  }

  function init() {
    var headings = Array.prototype.slice.call(
      document.querySelectorAll('[data-fe-letters]')
    );
    if (!headings.length) return;

    // Reduced motion: leave the headings exactly as authored.
    if (reduce.matches) return;

    var used = headings.slice(0, MAX_HEADINGS);

    if (!('IntersectionObserver' in window)) {
      used.forEach(function (el) {
        if (split(el)) el.classList.add('fe-is-visible');
      });
      return;
    }

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('fe-is-visible');
          obs.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.2 }
    );

    used.forEach(function (el) {
      if (split(el)) observer.observe(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
